import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Modal,
  Input,
  Space,
  message,
  DatePicker,
  Select,
  Upload,
} from 'antd'
import axios from 'axios'
import dayjs from 'dayjs'
import { UploadOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import {
  PlusOutlined,
  EditOutlined,
  SearchOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import {
  CreateComplaintApplicationData,
  GetComplaintApplicationData,
  DeleteComplaintApplicationData,
} from '../../../AllAPI/StudentAPI'
import { validateImageUpload } from '../../../Validation/FileValidation'


const ComplaintApplication = () => {
  const formDataFromStorage = JSON.parse(
    sessionStorage.getItem('studentData') || '{}',
  )

  const RegId = formDataFromStorage.reg_id
  const BranchId = formDataFromStorage.branch_id
  const BoardingYear = formDataFromStorage.year

  const [data, setData] = useState([])
  const [searchText, setSearchText] = useState('')
  const [loading, setLoading] = useState(false)

  // FORM STATES
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState(null)
  const [complaintImage, setComplaintImage] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deleteId, setDeleteId] = useState(null)

  // FETCH Complaint DATA
  const fetchComplaintData = async () => {
    setLoading(true)
    try {
      const res = await axios.get(
        `${GetComplaintApplicationData}/${RegId}/${BoardingYear}`,
      )

      const formatted = res.data.map((item, index) => ({
        key: index + 1,
        id: item.id,
        subject: item.subject,
        description: item.description,
        complaint_date: item.complaint_date,
        status: item.status,
      }))

      setData(formatted)
    } catch (error) {
      message.error('Failed to load complaint applications')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchComplaintData()
  }, [])

  // OPEN ADD MODAL
  const handleOpenModal = () => {
    setSubject(null)
    setDescription('')
    setIsModalOpen(true)
  }

  // SAVE OR UPDATE FUNCTION
  const handleSave = async () => {
    if (!subject || !description || !complaintImage) {
      toast.error('Please fill all fields')
      return
    }

    const formData = new FormData()

    formData.append('subject', subject)
    formData.append('description', description)
    formData.append('upload_img', complaintImage)
    formData.append('complaint_date', new Date().toISOString())
    formData.append('reg_id', RegId)
    formData.append('branch_id', BranchId)
    formData.append('year', BoardingYear)

    try {
      setLoading(true)
      const res = await axios.post(CreateComplaintApplicationData, formData)
      if (res.data === 'SAVED') {
        toast.success('Complaint Submitted Successfully')
        fetchComplaintData()
        setIsModalOpen(false)
        setSubject('')
        setDescription('')
      }
    } catch (error) {
      toast.error('Failed to save complaint')
    } finally {
      setLoading(false)
    }
  }

  // STATUS LABEL
  const getStatusLabel = (status) => {
    if (status === 2)
      return (
        <span
          className="shadow"
          style={{
            color: 'green',
            backgroundColor: '#d2faedff',
            borderRadius: '10px',
            padding: '5px 10px',
          }}
        >
          Resolved
        </span>
      )
    if (status === 3)
      return (
        <span
          className="shadow"
          style={{
            color: 'red',
            backgroundColor: '#fad2d2ff',
            borderRadius: '10px',
            padding: '5px 10px',
          }}
        >
          Rejected
        </span>
      )
    if (status === 0)
      return (
        <span
          className="shadow"
          style={{
            color: '#ff7301ff',
            backgroundColor: '#f7fdbaff',
            borderRadius: '10px',
            padding: '5px 10px',
          }}
        >
          Pending
        </span>
      )
  }

  // TABLE COLUMNS
  const columns = [
    { title: 'No.', dataIndex: 'key', width: 60 },
    { title: 'Subject', dataIndex: 'subject' },
    { title: 'Complaint Date', dataIndex: 'complaint_date' },
    { title: 'Description', dataIndex: 'description' },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => getStatusLabel(status),
    },
    {
      title: 'Actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<DeleteOutlined />}
            danger
            size="small"
            onClick={() => openDeleteModal(record.id)}
            disabled={record.status === 2 || record.status === 3}
          />
        </Space>
      ),
    },
  ]

  // FILTER
  const filteredData = data.filter((item) =>
    (item.reason ?? '').toLowerCase().includes(searchText.toLowerCase()),
  )

  const openDeleteModal = (id) => {
    setDeleteId(id)
    setIsDeleteModalOpen(true)
  }
  // DELETE FUNCTION
  const confirmDelete = async () => {
    try {
      setLoading(true)
      await axios.delete(`${DeleteComplaintApplicationData}/${deleteId}`)

      toast.success('Complaint deleted')
      fetchComplaintData()
    } catch (error) {
      toast.error('Failed to delete complaint')
    } finally {
      setLoading(false)
      setIsDeleteModalOpen(false)
    }
  }

  const subjectOptions = [
    { value: 'Room Cleaning Issue', label: 'Room Cleaning Issue' },
    { value: 'Water Problem', label: 'Water Problem' },
    { value: 'Electricity Issue', label: 'Electricity Issue' },
    { value: 'Hostel Maintenance', label: 'Hostel Maintenance' },
    { value: 'Furniture Problem', label: 'Furniture Problem' },
    { value: 'Other', label: 'Other' },
  ]

  const handleImageUpload = (info) => {
    const file = info.fileList?.[0]?.originFileObj

    if (file) {
      setComplaintImage(file) // correct File object
    } else {
      toast.warn('Upload Image')
    }
  }

  return (
    <div className="form-container mainContainer">
      <div className="row p-0 m-0 pb-5">
        <div className="col-12 col-md-2"></div>
        <div className="col-12 col-md-8">
          <div className="header-row">
            <h2 className="page-title">Complaint Application</h2>
          </div>

          {/* Search + Add Button */}
          <div className="search-bar">
            <Input
              placeholder="Search here..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
            />

            <Button
              className="addButton mt-2 mt-md-0"
              icon={<PlusOutlined />}
              onClick={handleOpenModal}
              style={{ marginLeft: 10 }}
            >
              Apply for Complaint
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={filteredData}
            pagination={{ pageSize: 10 }}
            bordered
            loading={loading}
            scroll={{ x: 'max-content' }}
          />

          {/*SUBMIT MODAL */}
          <Modal
            title="Apply Complaint"
            open={isModalOpen}
            onCancel={() => setIsModalOpen(false)}
            footer={[
              <Button key="cancel" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>,
              <Button key="save" className="addButton" onClick={handleSave}>
                Submit
              </Button>,
            ]}
          >
            <div style={{ marginBottom: 12 }}>
              <label>Subject</label>
              <Select
                placeholder="Select Complaint Subject"
                size="large"
                value={subject}
                onChange={(value) => setSubject(value)}
                allowClear
                style={{ width: '100%' }}
              >
                {subjectOptions.map((item) => (
                  <Select.Option key={item.value} value={item.value}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label>Description</label>
              <Input.TextArea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter Description"
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label>Upload Receipt Image</label> <br />
              <Upload
  beforeUpload={validateImageUpload}
                beforeUpload={(file) => {
                  const isImage =
                    file.type === 'image/png' ||
                    file.type === 'image/jpeg' ||
                    file.type === 'image/jpg'

                  if (!isImage) {
                    message.error('Only PNG, JPG, JPEG files are allowed!')
                    return Upload.LIST_IGNORE
                  }

                  const isLt2MB = file.size / 1024 / 1024 < 2

                  if (!isLt2MB) {
                    toast.error('Image must be smaller than 2MB!')
                    return Upload.LIST_IGNORE
                  }

                  return false // prevent auto upload
                }}
                onChange={handleImageUpload}
                listType="picture"
                maxCount={1}
                accept=".png,.jpg,.jpeg"
                style={{ width: '100%' }}
              >
                <Button
                  icon={<UploadOutlined />}
                  style={{ width: '100%' }}
                  size="large"
                >
                  Upload
                </Button>
              </Upload>
            </div>
          </Modal>

          {/*DELETE MODAL */}
          <Modal
            title="Confirm Delete"
            open={isDeleteModalOpen}
            onCancel={() => setIsDeleteModalOpen(false)}
            footer={[
              <Button key="cancel" onClick={() => setIsDeleteModalOpen(false)}>
                Cancel
              </Button>,
              <Button
                key="delete"
                danger
                type="primary"
                onClick={confirmDelete}
              >
                Delete
              </Button>,
            ]}
          >
            <p>Are you sure you want to delete this complaint application?</p>
          </Modal>
        </div>
      </div>
    </div>
  )
}

export default ComplaintApplication
